// Which build is this, and is the API the same age as it?
//
// WHY THIS SCREEN EXISTS. Whether the site updated should be answerable
// without opening the Cloudflare dashboard. The shape is the Puzzle Site's
// About page.
//
// AND WHY IT ASKS THE SERVER TOO. A Pages deployment is two things that can go
// stale apart — the bundle a browser downloads, and the Functions that answer
// `/api/*`. On 2026-09-21 the site served a current bundle while every endpoint
// but one answered with the HTML shell, and the only reason anybody found out
// was a smoke check by hand. A stamp that reads only its own bundle would have
// said "updated" that morning, so this asks `/api/version` and prints whatever
// comes back, including the disappointing answers.
//
// PUBLIC, AND OUTSIDE `Shell`, for the reason `/lab/player/:id` is: `Shell`
// redirects anyone without a session, and a deploy you cannot sign in to is
// exactly when you need to read this.
//
// AND IN A WINDOW FROM THE TOP BAR. During development the quick answer is a
// small, always-available info icon in the top bar, showing just when the site
// was last updated and the build version, enough to confirm a push landed.
// So the top bar's window
// (`shell/AboutButton.tsx`) is the short answer, and this page keeps the long
// one; both ask the API through `useApiAnswer`.

import { useEffect, useState } from 'react';
import { ApiVersion } from '@fsp/domain';
import { buildInfo, shortSha } from '../buildInfo';
import { get, messageOf } from '../data/api';
import { BRAND } from '../branding';
import { Card, Chip, Heading, Line, Stack, Text, Title, type Tone } from '../kit/ui';
import '../styles/screens/account.css';

/** A local instant, or the raw string when it is not one. */
export function when(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString();
}

export function ago(iso: string): string {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return '';
  const mins = Math.round((Date.now() - at) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fsp-about-row">
      <Text tone="secondary">{label}</Text>
      <div className="fsp-about-value">{children}</div>
    </div>
  );
}

/** Long enough for a cold edge, short enough to be a verdict rather than a wait. */
const TIMEOUT_MS = 8000;

export type ApiState =
  { state: 'asking' } | { state: 'ok'; schema: string } | { state: 'failed'; why: string };

export function AboutScreen() {
  return (
    <div className="fsp-about">
      <Stack gap={5}>
        <Stack gap={1}>
          <Title>About</Title>
          <Text tone="secondary" block>
            {BRAND.name} — what is deployed here, so a push can be checked without opening
            Cloudflare.
          </Text>
        </Stack>
        <AboutCards />
      </Stack>
    </div>
  );
}

/** Whether `/api/version` answers, asked once on mount, never left hanging. */
export function useApiAnswer(): ApiState {
  const [api, setApi] = useState<ApiState>({ state: 'asking' });

  useEffect(() => {
    const controller = new AbortController();
    // A CHECK THAT CAN HANG IS NOT A CHECK. Measured on 2026-09-21: with the
    // API process stopped, the dev proxy holds the connection open and this
    // screen sat on "Asking…" indefinitely — the one answer worse than "not
    // answering", because it looks like work in progress rather than a verdict.
    // In production a broken deployment answers immediately with the HTML
    // shell, so this covers the slower half: a server that accepts and stalls.
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, TIMEOUT_MS);
    void (async () => {
      // `no-store`, because `/api/version` is cacheable for five minutes and
      // this screen is asked precisely when somebody doubts the deployment.
      const reply = await get('/version', ApiVersion, {
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timer);
      if (timedOut) {
        setApi({
          state: 'failed',
          why: `The API did not answer within ${TIMEOUT_MS / 1000} seconds.`,
        });
        return;
      }
      // Any other abort is this screen being left, not an answer.
      if (controller.signal.aborted) return;
      if (reply.status === 'ok') setApi({ state: 'ok', schema: reply.data.schema });
      else {
        // The honest failure, not a shrug. When the Functions are not being
        // reached at all this is where the HTML shell shows up as a parse
        // failure, which is the symptom worth being able to read on a phone.
        setApi({
          state: 'failed',
          why: messageOf(reply, 'The API did not answer.', 'The API could not be reached.'),
        });
      }
    })();
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, []);
  return api;
}

/** This build, and whether the API answers. */
function AboutCards() {
  const build = buildInfo();
  const api = useApiAnswer();
  const envTone: Tone =
    build.cfEnvironment === 'production'
      ? 'good'
      : build.cfEnvironment === 'preview'
        ? 'warn'
        : 'neutral';

  return (
    <Stack gap={5}>
      <Card padded>
        <Stack gap={3}>
          <Line spread>
            <Heading>This build</Heading>
            <Chip tone={envTone}>{build.cfEnvironment}</Chip>
          </Line>
          <Stack gap={2}>
            <Row label="Commit">
              <Text mono>{shortSha(build.sha)}</Text>
              {build.branch !== 'unknown' && (
                <span className="fsp-about-branch">
                  <Text size="xs" tone="secondary">
                    on {build.branch}
                  </Text>
                </span>
              )}
            </Row>
            {/* The relative age goes UNDER the instant rather than beside
                  it: at 390 the two together wrap mid-phrase and "5 min" ends
                  a line with "ago" starting the next. */}
            <Row label="Built">
              <div>{when(build.builtAt)}</div>
              <Text size="xs" tone="secondary" block>
                {ago(build.builtAt)}
              </Text>
            </Row>
            <Row label="Committed">
              <div>{when(build.committedAt)}</div>
              <Text size="xs" tone="secondary" block>
                {ago(build.committedAt)}
              </Text>
            </Row>
          </Stack>
          {build.sha === 'unknown' && (
            <Text size="xs" tone="secondary" block>
              No build stamp: this bundle was compiled by something that does not inject one — a dev
              server without git, or a test run.
            </Text>
          )}
        </Stack>
      </Card>

      <Card padded>
        <Stack gap={3}>
          <Line spread>
            <Heading>The API</Heading>
            {api.state === 'ok' && <Chip tone="good">answering</Chip>}
            {api.state === 'failed' && <Chip tone="bad">not answering</Chip>}
          </Line>
          {api.state === 'asking' && <Text tone="secondary">Asking…</Text>}
          {api.state === 'ok' && (
            <Row label="Contract">
              <Text mono>{api.schema}</Text>
            </Row>
          )}
          {api.state === 'failed' && (
            <Stack gap={1}>
              <Text block>{api.why}</Text>
              <Text size="xs" tone="secondary" block>
                The page you are reading loaded, so this is the Functions half of the deployment and
                not the site. The bundle above can be current while this is not.
              </Text>
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
